import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  FormControl,
  FormLabel,
  Heading,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WorkbookCard from "./WorkbookCard";
import WorkbookEmptyState from "./WorkbookEmptyState";
import {
  fetchOrganizationSurveysPayload,
  normalizeOrganizationSurveys,
} from "../organizationSurveysApi";
import {
  getWorkbookSummary,
  isNoWorkbookDataError,
  persistWorkbookOrganizationSurveyId,
  resolveWorkbookOrganizationSurveyId,
} from "../organizationWorkbookApi";
import { getWorkbookRoutePrefix, isReadOnlyWorkbookRoute, workbookPath } from "./workbookRouting";
import { formatOrganizationSurveyTitle } from "../../../utils/organizationSurveyHelper";

const sectionCards = (payload, keys) => {
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  return [];
};

const getPageId = (page) => page?.page_id ?? page?.id ?? null;

const getPageDescription = (page) =>
  page?.description ||
  "Description will appear here when workbook summary data is available.";

const getStrengthDescription = (page) =>
  page?.strength ||
  getPageDescription(page);

const getDevelopmentDescription = (page) =>
  page?.development ||
  getPageDescription(page);

const getInterpretationId = (page) =>
  page?.interpretation_id ?? page?.interpretationId ?? null;

const getSurveyId = (survey) =>
  survey?.organization_survey_id ?? survey?.id ?? null;

const WorkbookHubPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const routePrefix = getWorkbookRoutePrefix(location.pathname);
  const readOnly = isReadOnlyWorkbookRoute(location.pathname, location.state);
  const selectedOrganizationSurveyId = location.state?.organizationSurveyId ?? null;
  const [summary, setSummary] = useState(null);
  const [workbookSurveys, setWorkbookSurveys] = useState([]);
  const [organizationSurveyId, setOrganizationSurveyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [noWorkbookData, setNoWorkbookData] = useState(false);
  const [selectedSurveyUnavailable, setSelectedSurveyUnavailable] = useState(false);

  useEffect(() => {
    let active = true;

    const loadSummary = async () => {
      let isRestartingWithResolvedSurvey = false;

      try {
        setLoading(true);
        setError("");
        setNoWorkbookData(false);
        setSelectedSurveyUnavailable(false);
        const surveysPayload = await fetchOrganizationSurveysPayload({ organizationId });
        const surveys = normalizeOrganizationSurveys(surveysPayload);
        const nextWorkbookSurveys = surveys.filter(
          (survey) => survey?.workbook_usable === true
        );
        const resolvedSurveyId = await resolveWorkbookOrganizationSurveyId({
          routeStateOrganizationSurveyId: organizationSurveyId
            ? null
            : selectedOrganizationSurveyId,
          organizationSurveyId,
          organizationId,
          surveysPayload,
        });

        if (!active) return;
        setWorkbookSurveys(nextWorkbookSurveys);

        const resolvedSurveyIsUsable = nextWorkbookSurveys.some(
          (survey) => String(getSurveyId(survey)) === String(resolvedSurveyId)
        );
        if (!resolvedSurveyIsUsable) {
          setSelectedSurveyUnavailable(true);
          setSummary(null);
          return;
        }

        if (String(resolvedSurveyId) !== String(organizationSurveyId ?? "")) {
          if (!active) return;
          isRestartingWithResolvedSurvey = true;
          setOrganizationSurveyId(resolvedSurveyId);
          setSummary(null);
          return;
        }

        const data = await getWorkbookSummary(resolvedSurveyId);
        if (!active) return;
        setSummary(data);
      } catch (err) {
        if (!active) return;
        if (isNoWorkbookDataError(err)) {
          setSummary(null);
          setWorkbookSurveys([]);
          setNoWorkbookData(true);
          return;
        }
        setError(t("organization.workbook.load_error", "Could not load the workbook."));
      } finally {
        if (active && !isRestartingWithResolvedSurvey) setLoading(false);
      }
    };

    loadSummary();

    return () => {
      active = false;
    };
  }, [organizationId, selectedOrganizationSurveyId, organizationSurveyId]);

  const handleOrganizationSurveyChange = (event) => {
    const nextOrganizationSurveyId = event.target.value;

    if (!nextOrganizationSurveyId) return;
    if (String(nextOrganizationSurveyId) === String(organizationSurveyId ?? "")) return;

    setSelectedSurveyUnavailable(false);
    persistWorkbookOrganizationSurveyId(nextOrganizationSurveyId);
    setOrganizationSurveyId(nextOrganizationSurveyId);
    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        organizationSurveyId: nextOrganizationSurveyId,
        ...(readOnly ? { readOnly: true } : {}),
      },
    });
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  if (noWorkbookData) {
    return (
      <WorkbookEmptyState
        title={t(
          "organization.workbook.no_workbook_data_available",
          "No workbook data available yet."
        )}
        description={t(
          "organization.workbook.no_workbook_data_description",
          "Workbook data will become available after survey results are generated."
        )}
      />
    );
  }

  const strengthPages = sectionCards(summary, ["strengths", "strength_pages"]);
  const developmentPages = sectionCards(summary, ["development", "development_pages"]);
  const selectedWorkbookSurvey = workbookSurveys.find(
    (survey) => String(getSurveyId(survey)) === String(organizationSurveyId)
  );
  const selectedSurveyTitle = formatOrganizationSurveyTitle(
    selectedWorkbookSurvey || summary || {}
  );

  return (
    <VStack align="stretch" spacing={8}>
      <Box>
        {workbookSurveys.length > 0 ? (
          <FormControl maxW={{ base: "100%", md: "420px" }} mb={6}>
            <FormLabel>
              {t("organization.workbook.select_workbook", "Select Workbook")}
            </FormLabel>
            <Select
              placeholder={
                organizationSurveyId &&
                !workbookSurveys.some(
                  (survey) =>
                    String(getSurveyId(survey)) === String(organizationSurveyId)
                )
                  ? t("organization.workbook.current_workbook", "Current workbook")
                  : undefined
              }
              value={
                workbookSurveys.some(
                  (survey) =>
                    String(getSurveyId(survey)) === String(organizationSurveyId)
                )
                  ? organizationSurveyId ?? ""
                  : ""
              }
              onChange={handleOrganizationSurveyChange}
            >
              {workbookSurveys.map((survey) => (
                <option
                  key={getSurveyId(survey)}
                  value={getSurveyId(survey)}
                >
                  {formatOrganizationSurveyTitle(survey)}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <Heading size="lg">{t("organization.workbook.title", "Workbook")}</Heading>
        {selectedSurveyTitle ? (
          <Text fontWeight="semibold" mt={2}>
            {selectedSurveyTitle}
          </Text>
        ) : null}
        <Text color="gray.600" mt={2} maxW="3xl">
          {t(
            "organization.workbook.hub_intro",
            "Review the strongest and most important development areas from your workbook, then move into the interpretation and practice steps."
          )}
        </Text>
      </Box>

      {selectedSurveyUnavailable ? (
        <WorkbookEmptyState
          title={t(
            "organization.workbook.unavailable_title",
            "Workbook not available yet"
          )}
          description={t(
            "organization.workbook.unavailable_description",
            "This survey does not have enough data to generate a workbook yet."
          )}
        />
      ) : (
        <>
          <Box>
            <Heading size="md" mb={4}>
              {t("organization.workbook.strengths_title", "Your Strengths")}
            </Heading>
            {strengthPages.length === 0 ? (
              <WorkbookEmptyState
                title={t("organization.workbook.no_strength_pages_title", "No strength pages yet")}
                description={t(
                  "organization.workbook.no_strength_pages_description",
                  "Strength pages will appear here when workbook summary data is available."
                )}
              />
            ) : (
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                {strengthPages.map((page) => {
                  const pageId = getPageId(page);

                  return (
                    <WorkbookCard
                      key={pageId}
                      title={page.title}
                      areaLabel={page.area_label}
                      result={page.result}
                      description={getStrengthDescription(page)}
                      accentColor="green.500"
                    />
                  );
                })}
              </SimpleGrid>
            )}
          </Box>

          <Box>
            <Heading size="md" mb={4}>
              {t("organization.workbook.development_title", "Development Areas")}
            </Heading>
            {developmentPages.length === 0 ? (
              <WorkbookEmptyState
                title={t(
                  "organization.workbook.no_development_pages_title",
                  "No development pages yet"
                )}
                description={t(
                  "organization.workbook.no_development_pages_description",
                  "Development pages will appear here when workbook summary data is available."
                )}
              />
            ) : (
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                {developmentPages.map((page) => {
                  const pageId = getPageId(page);
                  const interpretationId = getInterpretationId(page);

                  return (
                    <WorkbookCard
                      key={pageId}
                      title={page.title}
                      areaLabel={page.area_label}
                      result={page.result}
                      description={getDevelopmentDescription(page)}
                      ctaLabel={t(
                        "organization.workbook.start_working_on_area",
                        "Start working on this area"
                      )}
                      ctaTo={pageId ? workbookPath(routePrefix, `pages/${pageId}`) : null}
                      ctaState={{
                        ...(readOnly ? { readOnly: true } : {}),
                        organizationSurveyId,
                        pageId,
                        ...(interpretationId ? { interpretationId } : {}),
                      }}
                      accentColor="orange.500"
                    />
                  );
                })}
              </SimpleGrid>
            )}
          </Box>
        </>
      )}
    </VStack>
  );
};

export default WorkbookHubPage;
